import * as THREE from 'three';
import { G, mesh } from './scene';
import { MH } from './hangar-scene';
/* ============================================================
   機庫的陳設
   ============================================================

   **這些東西存在的唯一理由是尺度。** 機體旁邊沒有任何已知大小的物件時，
   它是 4 公尺還是 40 公尺完全讀不出來 —— 那正是原本的機庫看起來
   「像個玩具站在虛空裡」的原因之一。

   所以每一樣都刻意做成**人類尺度**：貨櫃約 1.8 高、工具櫃 2.0、
   欄杆 1.1、線軸 1.2。機體 4.4 高，站在它們旁邊才有兩倍半人高的感覺。

   全部靠牆或靠邊放，**不進入機體周圍 5 單位內** —— 中央要留給機體與
   鏡頭旋轉，陳設擋在前面就本末倒置了。 */

/** 由 `hangar-scene.ts` 傳進來的 `add`，讓陳設掛在同一個 group 上。 */
export function hangarProps(add: (o: any) => any) {
  containers(add);
  toolRacks(add);
  gantries(add);
  railings(add);
  pipes(add);
}

/* ---------- 貨櫃堆 ---------- */
function containers(add) {
  /* 兩側各一堆，高度刻意不同。等高的堆疊會讀成「重複的方塊」，
     錯落才讀得出是一堆被隨手放置的東西。 */
  var spots = [
    [-9.6, -7.2, 0], [-9.6, -4.6, 1], [-8.2, -9.4, 0],
    [9.9, -6.4, 1], [9.9, -8.8, 0], [8.4, -4.2, 0]
  ];
  for (var i = 0; i < spots.length; i++) {
    var x = spots[i][0], z = spots[i][1], tall = spots[i][2];
    var g = new THREE.Group();
    g.position.set(x, -0.55, z);
    g.rotation.y = (i % 3) * 0.14 - 0.14;
    g.add(mesh(G.box, MH.deck, [2.2, 1.8, 1.5], [0, 0.9, 0]));
    g.add(mesh(G.box, MH.dark, [2.24, 0.12, 1.54], [0, 1.35, 0]));    // 上緣飾條
    g.add(mesh(G.box, MH.dark, [2.24, 0.12, 1.54], [0, 0.45, 0]));
    g.add(mesh(G.box, MH.warn, [0.5, 0.3, 0.06], [0.6, 0.95, 0.78])); // 側面標記
    if (tall) {                                                        // 疊第二層
      g.add(mesh(G.box, MH.deck, [2.0, 1.5, 1.4], [0.12, 2.55, 0.04]));
      g.add(mesh(G.box, MH.dark, [2.04, 0.1, 1.44], [0.12, 3.24, 0.04]));
    }
    add(g);
  }
}

/* ---------- 工具架與備件 ---------- */
function toolRacks(add) {
  [-1, 1].forEach(function (s) {
    var g = new THREE.Group();
    g.position.set(s * 7.4, -0.55, -2.2);
    g.rotation.y = s * 0.32;
    // 立柱與層板
    [-0.85, 0.85].forEach(function (x) { g.add(mesh(G.box, MH.rib, [0.14, 2.0, 0.14], [x, 1.0, 0])); });
    [0.5, 1.15, 1.8].forEach(function (y) { g.add(mesh(G.box, MH.deck, [1.9, 0.07, 0.7], [0, y, 0])); });
    // 層板上的備件：不同大小的塊，讀起來是零件不是裝飾
    g.add(mesh(G.box, MH.dark, [0.5, 0.4, 0.5], [-0.45, 0.75, 0]));
    g.add(mesh(G.cyl, MH.rib, [0.3, 0.5, 0.3], [0.4, 0.8, 0.05]));
    g.add(mesh(G.box, MH.dark, [0.7, 0.28, 0.45], [0.1, 1.44, 0]));
    g.add(mesh(G.box, MH.glow, [0.3, 0.1, 0.04], [-0.6, 1.42, 0.36]));
    add(g);
  });
}

/* ---------- 維修吊臂 ---------- */
function gantries(add) {
  /* 從天花板垂下的兩支液壓臂，停在機體肩線外側。
     **刻意不碰到機體**（x = ±3.1，機體肩外緣約 ±1.6）——
     碰到就要做「正在維修」的動畫，那是另一件事；
     停在旁邊反而讀成「剛收工」，靜態畫面也成立。 */
  [-1, 1].forEach(function (s) {
    var g = new THREE.Group();
    g.position.set(s * 3.3, 0, -1.5);
    g.add(mesh(G.cyl, MH.rib, [0.26, 3.4, 0.26], [0, 5.4, 0]));            // 主柱
    g.add(mesh(G.box, MH.deck, [0.5, 0.5, 0.5], [0, 3.7, 0]));             // 關節
    var arm = new THREE.Group();
    arm.position.set(0, 3.7, 0); arm.rotation.z = s * 0.42; arm.rotation.x = -0.2;
    arm.add(mesh(G.cyl, MH.deck, [0.22, 1.9, 0.22], [0, -0.95, 0]));
    arm.add(mesh(G.cyl, MH.rib, [0.1, 1.5, 0.1], [s * 0.16, -0.95, 0.12])); // 液壓桿
    arm.add(mesh(G.box, MH.dark, [0.44, 0.4, 0.6], [0, -1.95, 0]));         // 夾具
    arm.add(mesh(G.box, MH.glow, [0.16, 0.06, 0.16], [0, -2.14, 0]));
    g.add(arm);
    add(g);
  });
}

/* ---------- 欄杆與平台 ---------- */
function railings(add) {
  /* 站台後方的走道與欄杆。欄杆是最強的尺度線索 ——
     它的高度（1.1）是所有人都知道的，看到機體肩膀高過它三倍就懂了。 */
  [-1, 1].forEach(function (s) {
    var g = new THREE.Group();
    g.position.set(s * 6.2, -0.55, -6.5);
    g.add(mesh(G.box, MH.deck, [5.0, 0.22, 2.4], [0, 1.5, 0]));             // 走道板
    [-2.2, 2.2].forEach(function (x) { g.add(mesh(G.box, MH.rib, [0.2, 1.5, 0.2], [x, 0.75, 0.9])); });
    // 欄杆：上桿 + 中桿 + 立柱
    g.add(mesh(G.box, MH.rib, [5.0, 0.08, 0.08], [0, 2.72, 1.1]));
    g.add(mesh(G.box, MH.rib, [5.0, 0.06, 0.06], [0, 2.2, 1.1]));
    for (var x2 = -2.3; x2 <= 2.3; x2 += 1.15) {
      g.add(mesh(G.box, MH.rib, [0.08, 1.1, 0.08], [x2, 2.17, 1.1]));
    }
    add(g);
  });
}

/* ---------- 管線 ---------- */
function pipes(add) {
  /* 沿側牆的橫向管線 + 一捲線軸。工業場地的視覺語彙裡管線是最便宜的一種：
     幾根 cylinder 就能讓平牆讀起來有系統在運作。 */
  [-1, 1].forEach(function (s) {
    var x = s * 13.4;
    [4.2, 4.9, 5.6].forEach(function (y, i) {
      var p = mesh(G.cyl, i === 1 ? MH.deck : MH.rib, [0.3, 20, 0.3], [x, y - 0.55, -3], [Math.PI / 2, 0, 0]);
      add(p);
    });
    // 管夾
    for (var z = -11; z <= 5; z += 4) {
      add(mesh(G.box, MH.dark, [0.5, 2.0, 0.3], [x, 4.35, z]));
    }
    // 線軸
    var r = new THREE.Group();
    r.position.set(s * 11.2, -0.55, -2.2);
    r.add(mesh(G.cyl, MH.deck, [1.2, 0.16, 1.2], [0, 0.6, 0], [0, 0, Math.PI / 2]));
    r.add(mesh(G.cyl, MH.deck, [1.2, 0.16, 1.2], [0.55, 0.6, 0], [0, 0, Math.PI / 2]));
    r.add(mesh(G.cyl, MH.dark, [0.9, 0.5, 0.9], [0.28, 0.6, 0], [0, 0, Math.PI / 2]));
    r.add(mesh(G.box, MH.rib, [0.14, 0.7, 0.14], [-0.1, 0.3, 0]));
    r.add(mesh(G.box, MH.rib, [0.14, 0.7, 0.14], [0.66, 0.3, 0]));
    add(r);
  });
}
